import React, { useMemo } from "react";
import {
  Button,
  Flex,
  Modal,
  ModalBody,
  ModalContent,
  ModalFooter,
  ModalHeader,
  Text,
} from "@appsmith/ads";
import { useDispatch, useSelector } from "react-redux";
import {
  getDeleteMembersLoading,
  getSelectedMembers,
  getMembersList,
} from "selectors/MembersSelectors";
import { deleteMembers } from "actions/MembersActions";
import { MemberQueryParams } from "types/MembersTypes";
import {
  createMessage,
  DELETE_MEMBERS_CONFIRM_BUTTON,
  DELETE_MEMBERS_CONFIRM_TEXT,
  DELETE_MEMBERS_MODAL_HEADER,
  DELETE_MEMBERS_SEAT_WARNING,
  DELETE_MEMBERS_SYNC_WARNING,
  DELETE_MEMBERS_SEATED_BADGE,
} from "constants/Messages";

interface DeleteMembersModalProps {
  isOpen: boolean;
  onClose: () => void;
  queryParams: MemberQueryParams;
}

const DeleteMembersModal = ({
  isOpen,
  onClose,
  queryParams,
}: DeleteMembersModalProps) => {
  const dispatch = useDispatch();
  const selectedEmails = useSelector(getSelectedMembers);
  const memberList = useSelector(getMembersList);
  const isLoading = useSelector(getDeleteMembersLoading);

  const selectedMembersWithDetails = useMemo(() => {
    const emailSet = new Set(selectedEmails.map((e) => e.toLowerCase()));
    return memberList.filter((m) => emailSet.has(m.email.toLowerCase()));
  }, [selectedEmails, memberList]);

  const seatedCount = useMemo(
    () => selectedMembersWithDetails.filter((m) => m.seatAssigned).length,
    [selectedMembersWithDetails],
  );

  const handleConfirmDelete = () => {
    dispatch(deleteMembers({ ...queryParams, emails: selectedEmails }));
  };

  if (!isOpen) return null;

  return (
    <Modal
      onOpenChange={(open) => {
        if (!open && !isLoading) onClose();
      }}
      open={isOpen}
    >
      <ModalContent className="!w-[560px]">
        <ModalHeader>{createMessage(DELETE_MEMBERS_MODAL_HEADER)}</ModalHeader>
        <ModalBody>
          <Flex flexDirection="column" gap="spaces-4">
            <Text kind="body-m">
              {createMessage(
                DELETE_MEMBERS_CONFIRM_TEXT,
                selectedEmails.length,
              )}
            </Text>
            {seatedCount > 0 && (
              <Text color="var(--ads-v2-color-fg-warning)" kind="body-m">
                {createMessage(DELETE_MEMBERS_SEAT_WARNING, seatedCount)}
              </Text>
            )}
            <Flex
              bg="var(--ads-v2-color-bg-subtle)"
              borderRadius="var(--ads-v2-border-radius)"
              flexDirection="column"
              gap="spaces-1"
              maxHeight="200px"
              overflowY="auto"
              padding="spaces-3"
            >
              <Text color="var(--ads-v2-color-fg-muted)" kind="body-s">
                {createMessage(DELETE_MEMBERS_SYNC_WARNING)}
              </Text>
            </Flex>
            <Flex
              border="1px solid var(--ads-v2-color-border)"
              borderRadius="var(--ads-v2-border-radius)"
              flexDirection="column"
              maxHeight="200px"
              overflowY="auto"
            >
              {selectedEmails.map((email) => {
                const member = selectedMembersWithDetails.find(
                  (m) => m.email.toLowerCase() === email.toLowerCase(),
                );
                return (
                  <Flex
                    alignItems="center"
                    borderBottom="1px solid var(--ads-v2-color-border)"
                    justifyContent="space-between"
                    key={email}
                    padding="spaces-3"
                  >
                    <Text kind="body-s">{email}</Text>
                    {member?.seatAssigned && (
                      <Text
                        color="var(--ads-v2-color-fg-warning)"
                        kind="body-s"
                      >
                        {createMessage(DELETE_MEMBERS_SEATED_BADGE)}
                      </Text>
                    )}
                  </Flex>
                );
              })}
            </Flex>
          </Flex>
        </ModalBody>
        <ModalFooter>
          <Flex gap="spaces-3" justifyContent="flex-end">
            <Button
              isDisabled={isLoading}
              kind="secondary"
              onClick={onClose}
              size="md"
            >
              Cancel
            </Button>
            <Button
              isLoading={isLoading}
              kind="error"
              onClick={handleConfirmDelete}
              size="md"
            >
              {createMessage(
                DELETE_MEMBERS_CONFIRM_BUTTON,
                selectedEmails.length,
              )}
            </Button>
          </Flex>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
};

export { DeleteMembersModal };
